import { useTranslation } from 'react-i18next'
import { LinkIcon } from 'lucide-react'
import { toast } from 'sonner'

import { Checkbox } from '../../../ui/checkbox'
import { Badge } from '../../../ui/badge'
import { InfluencerData } from '../../../../core/interface/chronique.interface'
import socialIcons from '../../../../core/utils/media-platform.utils'
import statusColors from '../../../../core/utils/mapping-status-influencer-color.util'
import InfluencerTableTextWrapper from './InfluencerTableTextWrapper'
import { TableViewTypeInfluencerEnum } from '../../../../core/enums/table-view-type-influencer.enum'
import { ReactComponent as CopyPasteIcon } from '../../../../assets/icons/app/copy-paste.icon.svg'
import { ReactComponent as ArrowTopIcon } from '../../../../assets/icons/app/arrow-top.icon.svg'
import { TooltipProvider } from '../../../ui/tooltip'

interface Props {
  viewType: TableViewTypeInfluencerEnum
  data: InfluencerData[]
  selectedUsers: InfluencerData[]
  onUserSelect: (user: InfluencerData, selected: boolean) => void
  onSelectAll: (selected: boolean) => void
  onUserClick: (user: InfluencerData) => void
  userSelected: InfluencerData | null
  proximitySort?: 'asc' | 'desc'
  onProximitySortChange?: (sort: 'asc' | 'desc') => void
}

const InfluencerTableView = ({
  viewType,
  data,
  selectedUsers,
  onUserSelect,
  onSelectAll,
  onUserClick,
  userSelected,
  proximitySort,
  onProximitySortChange,
}: Props) => {
  const { t } = useTranslation()
  const allSelected = data.length > 0 && selectedUsers.length === data.length

  const handleProximitySort = () => {
    if (!onProximitySortChange) return

    if (proximitySort === 'asc') {
      onProximitySortChange('desc')
    } else if (proximitySort === 'desc') {
      onProximitySortChange('asc')
    }
  }

  const onClickMailCopyButton = (email: string) => {
    navigator.clipboard
      .writeText(email)
      .then(() => {
        toast.success(t('chroniqueurView.tableView.body.emailCopied'))
      })
      .catch(() => {})
  }
  return (
    <TooltipProvider>
      <div className="overflow-auto w-full">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 sticky top-0 z-10">
            <tr>
              {viewType !== TableViewTypeInfluencerEnum.INFLUENCERS_VIEW && (
                <InfluencerTableTextWrapper as="th">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(checked) => onSelectAll(!!checked)}
                  />
                </InfluencerTableTextWrapper>
              )}
              {viewType !== TableViewTypeInfluencerEnum.INFLUENCERS_VIEW && (
                <InfluencerTableTextWrapper as="th">
                  <button
                    type="button"
                    onClick={handleProximitySort}
                    className="flex items-center gap-1 hover:bg-gray-100 px-1 py-1 rounded transition-colors"
                    disabled={!onProximitySortChange}
                  >
                    <span>
                      {t('chroniqueurView.tableView.header.proximity')}
                    </span>
                    <div className="flex flex-col items-center">
                      <ArrowTopIcon
                        className={`h-3 w-3 transition-transform rotate-0 ${
                          proximitySort === 'asc'
                            ? 'text-gray-900'
                            : 'text-gray-400'
                        }`}
                      />
                      <ArrowTopIcon
                        className={`h-3 w-3 transition-transform rotate-180 ${
                          proximitySort === 'asc'
                            ? 'text-gray-400'
                            : 'text-gray-900'
                        }`}
                      />
                    </div>
                  </button>
                </InfluencerTableTextWrapper>
              )}
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.userStatus')}
              </InfluencerTableTextWrapper>
              {viewType === TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW && (
                <InfluencerTableTextWrapper as="th">
                  {t('chroniqueurView.tableView.header.statusForThisChronique')}
                </InfluencerTableTextWrapper>
              )}
              {viewType !== TableViewTypeInfluencerEnum.INFLUENCERS_VIEW && (
                <InfluencerTableTextWrapper as="th">
                  {t('chroniqueurView.tableView.header.alreadyReviewed')}
                </InfluencerTableTextWrapper>
              )}
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.chroniques')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th" width="90px">
                {t('chroniqueurView.tableView.header.lastParticipation')}
              </InfluencerTableTextWrapper>

              {/*  */}
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.email')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.gleephPseudo')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.gleephProfile')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.gleephFollowers')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.reviews')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th" width="90px">
                {t('chroniqueurView.tableView.header.library')}
              </InfluencerTableTextWrapper>
              {viewType === TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW && (
                <InfluencerTableTextWrapper as="th">
                  {t('chroniqueurView.tableView.header.formCompleted')}
                </InfluencerTableTextWrapper>
              )}
              {viewType === TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW && (
                <InfluencerTableTextWrapper as="th">
                  {t('chroniqueurView.tableView.header.promise')}
                </InfluencerTableTextWrapper>
              )}
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.influence')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.socialNetworks')}
              </InfluencerTableTextWrapper>
              <InfluencerTableTextWrapper as="th">
                {t('chroniqueurView.tableView.header.profession')}
              </InfluencerTableTextWrapper>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 ">
            {data.map((user: InfluencerData) => {
              const isSelected = selectedUsers.some(
                (u) => u.influencer.userid === user.influencer.userid,
              )
              const isHighlighted =
                userSelected?.influencer.userid === user.influencer.userid ||
                isSelected

              return (
                <tr
                  key={user.influencer.userid}
                  className={`hover:bg-gray-50 cursor-pointer transition-colors ${
                    isHighlighted
                      ? 'bg-[#BB99FD80] border-l-4 border-blue-500'
                      : ''
                  }`}
                  onClick={() => onUserClick(user)}
                >
                  {viewType !==
                    TableViewTypeInfluencerEnum.INFLUENCERS_VIEW && (
                    <td
                      className="px-2 py-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked: boolean) =>
                          onUserSelect(user, !!checked)
                        }
                      />
                    </td>
                  )}
                  {viewType !==
                    TableViewTypeInfluencerEnum.INFLUENCERS_VIEW && (
                    <InfluencerTableTextWrapper as="td">
                      {user.affinity}
                    </InfluencerTableTextWrapper>
                  )}
                  <InfluencerTableTextWrapper as="td" width="125px">
                    <Badge
                      variant="outline"
                      className={
                        statusColors[user.influencer.status] ||
                        'bg-gray-100 text-gray-800 border-gray-200'
                      }
                    >
                      {t(`chroniqueurView.status.${user.influencer.status}`)}
                    </Badge>
                  </InfluencerTableTextWrapper>
                  {viewType ===
                    TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW && (
                    <InfluencerTableTextWrapper as="td" width="100px">
                      {user.current_influencedbook_status ? (
                        <Badge
                          variant="outline"
                          className={
                            statusColors[user.current_influencedbook_status] ||
                            'bg-gray-100 text-gray-800 border-gray-200'
                          }
                        >
                          {t(
                            `chroniqueurView.chronique.${user.current_influencedbook_status}`,
                          )}
                        </Badge>
                      ) : (
                        t('chroniqueurView.tableView.body.notAvailable')
                      )}
                    </InfluencerTableTextWrapper>
                  )}
                  {viewType !==
                    TableViewTypeInfluencerEnum.INFLUENCERS_VIEW && (
                    <InfluencerTableTextWrapper as="td">
                      {user.already_influenced_for_publisher
                        ? t('chroniqueurView.tableView.body.yes')
                        : t('chroniqueurView.tableView.body.no')}
                    </InfluencerTableTextWrapper>
                  )}
                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.influenced_book_done}
                  </InfluencerTableTextWrapper>
                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.last_participation}
                  </InfluencerTableTextWrapper>
                  <InfluencerTableTextWrapper
                    as="td"
                    width="60px"
                    tooltipText={user.influencer.email}
                  >
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClickMailCopyButton(user.influencer.email)
                      }}
                      className="p-1"
                    >
                      <CopyPasteIcon />
                    </button>
                    {user.influencer.email}
                  </InfluencerTableTextWrapper>
                  <InfluencerTableTextWrapper
                    as="td"
                    tooltipText={user.influencer.pseudonym}
                  >
                    {user.influencer.pseudonym}
                  </InfluencerTableTextWrapper>
                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.profile && (
                      <a
                        onClick={(e) => e.stopPropagation()}
                        href={user.influencer.profile}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <LinkIcon size={14} />
                      </a>
                    )}
                  </InfluencerTableTextWrapper>
                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.followers}
                  </InfluencerTableTextWrapper>

                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.gleeph_reviews}
                  </InfluencerTableTextWrapper>
                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.library_size}
                  </InfluencerTableTextWrapper>

                  {viewType ===
                    TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW && (
                    <InfluencerTableTextWrapper as="td">
                      {user.form_completed
                        ? t('chroniqueurView.tableView.body.yes')
                        : t('chroniqueurView.tableView.body.no')}
                    </InfluencerTableTextWrapper>
                  )}
                  {viewType ===
                    TableViewTypeInfluencerEnum.CHRONIQUEUR_VIEW && (
                    <td className="px-2 py-2">
                      <div className="grid grid-cols-4 grid-rows-2 gap-x-4 gap-y-2">
                        {user.promises?.map((promise) => {
                          const SocialIcon =
                            socialIcons[promise.platform_obj.platform]
                          if (!SocialIcon) return null
                          if (!promise.url) {
                            return (
                              <div
                                key={`${promise.platform_obj.id}-${promise.type}`}
                                className="opacity-50 cursor-not-allowed"
                              >
                                <SocialIcon className="h-4 w-4" />
                              </div>
                            )
                          }
                          return (
                            <a
                              href={promise.url}
                              target="_blank"
                              key={`${promise.platform_obj.id}-${promise.type}`}
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <SocialIcon className="h-4 w-4" />
                            </a>
                          )
                        })}
                      </div>
                    </td>
                  )}
                  <InfluencerTableTextWrapper as="td">
                    {user.influencer.influence_degree}
                  </InfluencerTableTextWrapper>
                  <td className="px-2 py-2">
                    <div className="grid grid-cols-4 grid-rows-2 gap-x-4 gap-y-2">
                      {user.influencer.social_media?.map((social) => {
                        const SocialIcon =
                          socialIcons[social.social_media.platform]
                        if (!SocialIcon) return null
                        return (
                          <a
                            href={social.url}
                            target="_blank"
                            key={social.url}
                            rel="noreferrer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <SocialIcon className="h-4 w-4" />
                          </a>
                        )
                      })}
                    </div>
                  </td>
                  <InfluencerTableTextWrapper
                    as="td"
                    tooltipText={user.influencer.profession ?? undefined}
                  >
                    {user.influencer.profession}
                  </InfluencerTableTextWrapper>
                </tr>
              )
            })}
          </tbody>
        </table>

        {data.length === 0 && (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-gray-500 text-lg">
                {t('chroniqueurView.tableView.body.noUserFound')}
              </div>
              <div className="text-gray-400 text-sm mt-2">
                {t('chroniqueurView.tableView.body.tryEditingFilters')}
              </div>
            </div>
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}

export default InfluencerTableView
